import type { FC } from "react";
import {
	ClientPortfolioInstructionHoldingTypeEnum,
	ClientPortfolioInvestmentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { toolHasActivePeriodicPurchaseInstructions } from "web_myplann/Portfolio/Helpers/activePurchaseInstructions";
import type {
	NormalizedToolOfKind,
	ToolKind,
	ToolRowCommonProps,
} from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { useToolSummary } from "web_myplann/Portfolio/Helpers/useToolSummary";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { numberFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import { ToolRowWrapper } from "./ToolRowWrapper";

const PageResources = Resources.PortfolioDetail;

const getRecurringValue = (
	tool: NormalizedToolOfKind<typeof ToolKind.Crypto>,
	t: (key: string) => string,
): string => {
	if (tool.monthlyPayment === 0) {
		return EMPTY_DETAIL_FIELD;
	}

	const frequency = t(
		PageResources.ToolHistory.Fund.Frequency[
			ClientPortfolioInvestmentFrequencyEnum.Monthly
		],
	).toLowerCase();
	const formattedAmount = numberFormatter.format(
		Math.round(tool.monthlyPayment),
	);

	return `${formattedAmount} ${t(Resources.Common.CurrencySign)} ${frequency}`;
};

export const CryptoToolRow: FC<ToolRowCommonProps<typeof ToolKind.Crypto>> = ({
	tool,
	portfolioId,
	toolsSummary,
	expanded,
	onToggle,
	noDivider,
}) => {
	const { t } = useResource();
	const { totalValue, totalInvested, totalCount } = useToolSummary(
		tool.id,
		toolsSummary,
	);

	const item = tool.raw.cryptocurrencyInstructions[0];
	if (!item) return null;

	const hasActivePurchases = toolHasActivePeriodicPurchaseInstructions(tool);
	const shouldShowRecurring = hasActivePurchases && tool.monthlyPayment > 0;

	const holdingTypeLabel = t(
		PageResources.ToolHistory.Cryptocurrency.HoldingTypeShort[
			item.holdingType ?? ClientPortfolioInstructionHoldingTypeEnum.OwnWallet
		],
	);

	return (
		<ToolRowWrapper
			label={`${item.name} (${tool.identification}) - ${holdingTypeLabel}`}
			totalValue={totalValue}
			expanded={expanded}
			onToggle={onToggle}
			noDivider={noDivider}
			portfolioId={portfolioId}
			historyTarget={{ toolId: tool.id }}
		>
			<DataRow
				label={t(PageResources.ToolHistory.Cryptocurrency.holdingType)}
				value={t(
					PageResources.ToolHistory.Cryptocurrency.HoldingType[
						item.holdingType ??
							ClientPortfolioInstructionHoldingTypeEnum.OwnWallet
					],
				)}
			/>
			<DataRow
				label={t(PageResources.ToolHistory.Cryptocurrency.amount)}
				value={totalCount}
			/>
			{shouldShowRecurring && (
				<DataRow
					label={t(PageResources.SharedLabels.recurringPayment)}
					value={getRecurringValue(tool, t)}
				/>
			)}
			<DataRow
				label={t(PageResources.SharedLabels.investedAll)}
				value={totalInvested}
			/>
			<DataRow
				label={t(PageResources.SharedLabels.currentValue)}
				value={totalValue}
			/>
		</ToolRowWrapper>
	);
};
